<template>
  <div :class="containerClass">
    <page-header :title="$route.params.id ? 'Edit Research' : 'Post Research'" :container-class="null" />

    <div class="page-section">
      <div class="card">
        <form-wizard
          title=""
          subtitle=""
          @on-complete="onComplete"
          shape="tab"
          :color="APP_DARK_COLOR"
          ref="researchForm"
          class="tab-fwizard"
        >
          <tab-content title="Basic Info" icon="fas fa-info-circle" :before-change="() => onNext('basicInfoForm')">
            <research-basic-info-form :research-data="research" ref="basicInfoForm" />
          </tab-content>

          <tab-content title="Target Audience" icon="fas fa-user" :before-change="() => onNext('audienceForm')">
            <research-audience-form
              :research-data="research"
              :all-universities="allUnis"
              :are-unis-loading="areUnisLoading"
              :all-programs="allPrograms"
              :are-programs-loading="areProgramsLoading"
              :all-orgs="allOrgs"
              :are-orgs-loading="areOrgsLoading"
              ref="audienceForm"
            />
          </tab-content>

          <tab-content title="Benefits & Perks" icon="fas fa-hand-holding-medical" :before-change="() => onNext('benefitsForm')">
            <research-benefits-form :research-data="research" :career-options="careerOptions" ref="benefitsForm" />
          </tab-content>

          <tab-content title="Qualifications" icon="fas fa-graduation-cap" :before-change="() => onNext('qualificationsForm')">
            <research-qualifications-form :research-data="research" ref="qualificationsForm" />
          </tab-content>

          <tab-content title="Preferences" icon="fas fa-tasks" :before-change="() => onNext('preferencesForm')">
            <research-preferences-form :research-data="research" ref="preferencesForm" />
          </tab-content>

          <template v-slot:prev>
            <button class="btn btn-dark btn-normal" type="dark" :disabled="isLoading" style="min-width: 120px">Back</button>
          </template>

          <template v-slot:next>
            <b-button type="submit" variant="dark" :disabled="isLoading" style="min-width: 120px" class="btn-normal">
              <i v-if="isLoading" class="fas fa-circle-notch fa-spin"></i>
              <span v-else>Next</span>
            </b-button>
          </template>

          <template v-slot:finish>
            <button class="btn btn-primary btn-normal" type="primary" :disabled="isLoading" style="min-width: 120px">
              <i v-if="isLoading" class="fas fa-circle-notch fa-spin"></i>
              <span v-else>{{ $route.params.id ? 'Update' : 'Post Research' }}</span>
            </button>
          </template>
        </form-wizard>
      </div>
    </div>
  </div>
</template>

<script>
import { get } from 'lodash';
import { mapActions, mapGetters } from 'vuex';

import Page from '../../../components/Page.vue';
import PageHeader from '../../../components/Ui/PageHeader.vue';
import ResearchBasicInfoForm from './ResearchBasicInfoForm.vue';
import ResearchAudienceForm from './ResearchAudienceForm.vue';
import ResearchBenefitsForm from './ResearchBenefitsForm.vue';
import ResearchQualificationsForm from './ResearchQualificationsForm.vue';
import ResearchPreferencesForm from './ResearchPreferencesForm.vue';
import { APP_DARK_COLOR, USER_ROLES } from '../../../common/constants';

export default {
  components: {
    PageHeader,
    ResearchBasicInfoForm,
    ResearchAudienceForm,
    ResearchBenefitsForm,
    ResearchQualificationsForm,
    ResearchPreferencesForm,
  },
  extends: Page,

  data() {
    return {
      APP_DARK_COLOR,
      USER_ROLES,
      isLoading: false,
      areUnisLoading: false,
      areProgramsLoading: false,
      areOrgsLoading: false,
      allUnis: [],
      allPrograms: [],
      allOrgs: [],
      careerOptions: [],
      research: this.getDefaultResearch(),
    };
  },

  computed: {
    ...mapGetters('auth', ['getLoggedInUser']),
  },

  methods: {
    ...mapActions('research', ['getResearch', 'createResearch', 'updateResearch']),
    ...mapActions('uni', ['getAllUnis']),
    ...mapActions('program', ['getAllPortalPrograms']),
    ...mapActions('organization', ['getAllOrganizations']),
    ...mapActions('careerPreference', ['getAllCareerPreferences']),
    get,

    getDefaultResearch() {
      return {
        org: null,
        title: '',
        description: '',
        is_active: true,
        type: null,
        schedule: null,
        application_link: '',
        faculty_name: '',
        department: '',
        location: '',
        is_remote: false,
        is_paid: false,
        stipend: null,
        time_commitment: '',
        eligibility: '',
        deadline: null,
        universities: [],
        associated_programs: [],
        address: '',
        city: '',
        state: '',
        zipcode: '',
        country: 'United States',
        minimum_pay: null,
        maximum_pay: null,
        pay_rate: null,
        no_of_hires: null,
        benefits: [],
        other_benefits: null,
        offers_career_advancement: null,
        career_offerings: [],
        other_career_offering: null,
        career_offerings_details: '',
        caregiving_experience: '',
        required_certs: [],
        other_required_certs: null,
        required_records: [],
        other_required_records: null,
        resume_required: null,
        email_contact_allowed: null,
        contact_email: null,
        phone_contact_allowed: null,
        contact_phone: null,
      };
    },

    onNext(formRef) {
      let validation = { isValid: false, formData: {} };
      if (this.$refs[formRef]) {
        validation = this.$refs[formRef].validateFormData();
        this.research = { ...this.research, ...validation.formData };
      }
      if (!validation.isValid) {
        this.makeToast({ variant: 'danger', msg: 'Please fill the form correctly.' });
      }
      return validation.isValid;
    },

    async fetchUnis() {
      this.areUnisLoading = true;
      try {
        const response = await this.getAllUnis({ limit: 3000, basic: true });
        this.allUnis = get(response, 'data.results', response.data) || [];
      } catch (e) {
        this.allUnis = [];
      }
      this.areUnisLoading = false;
    },

    async fetchPrograms() {
      this.areProgramsLoading = true;
      try {
        const response = await this.getAllPortalPrograms({ limit: 1000 });
        this.allPrograms = get(response, 'data.results', response.data) || [];
      } catch (e) {
        this.allPrograms = [];
      }
      this.areProgramsLoading = false;
    },

    async fetchOrgs() {
      this.areOrgsLoading = true;
      try {
        const response = await this.getAllOrganizations({});
        this.allOrgs = get(response, 'data.results', response.data) || [];
      } catch (e) {
        this.allOrgs = [];
      }
      this.areOrgsLoading = false;
    },

    async fetchCareerOptions() {
      try {
        const response = await this.getAllCareerPreferences({ limit: 1000 });
        this.careerOptions = get(response, 'data.results', response.data) || [];
      } catch (e) {
        this.careerOptions = [];
      }
    },

    async fetchResearch() {
      try {
        const response = await this.getResearch(this.$route.params.id);
        const item = response.data;
        this.research = {
          ...this.getDefaultResearch(),
          ...item,
          org: get(item, 'ext_posted_by.id', null),
          universities: (item.universities || []).map((uni) => uni.id),
          associated_programs: (item.associated_programs || []).map((p) => p.id),
          career_offerings: (item.career_offerings || []).map((c) => c.id),
          benefits: item.benefits || [],
          required_certs: item.required_certs || [],
          required_records: item.required_records || [],
        };
      } catch (e) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
    },

    async onComplete() {
      this.isLoading = true;
      try {
        const data = {
          ...this.research,
          org: this.getLoggedInUser.role === USER_ROLES.SUPER_ADMIN ? this.research.org : null,
          other_benefits: (this.research.benefits || []).includes('other') ? this.research.other_benefits : null,
          career_offerings: this.research.offers_career_advancement
            ? (this.research.career_offerings || []).filter((c) => c !== 'other')
            : [],
          other_career_offering:
            this.research.offers_career_advancement && (this.research.career_offerings || []).includes('other')
              ? this.research.other_career_offering
              : null,
          career_offerings_details: this.research.offers_career_advancement
            ? this.research.career_offerings_details
            : null,
          other_required_certs: (this.research.required_certs || []).includes('other')
            ? this.research.other_required_certs
            : null,
          other_required_records: (this.research.required_records || []).includes('other')
            ? this.research.other_required_records
            : null,
          contact_email: this.research.email_contact_allowed ? this.research.contact_email : null,
          contact_phone: this.research.phone_contact_allowed ? this.research.contact_phone : null,
        };

        if (this.$route.params.id) {
          await this.updateResearch({ id: this.$route.params.id, data });
          this.makeToast({ variant: 'success', msg: 'Research Updated' });
        } else {
          await this.createResearch(data);
          this.makeToast({ variant: 'success', msg: 'Research Posted' });
        }
        setTimeout(() => this.$router.push({ name: 'research-management-list' }), 250);
      } catch (err) {
        const msg =
          get(err, 'response.status') === 403
            ? "You can't edit a research listing you don't own."
            : this.$t('generalMsgs.genErrorMsg');
        this.makeToast({ variant: 'danger', msg });
      }
      this.isLoading = false;
    },
  },

  async mounted() {
    this.fetchUnis();
    this.fetchPrograms();
    this.fetchCareerOptions();
    if (this.getLoggedInUser.role === USER_ROLES.SUPER_ADMIN) {
      this.fetchOrgs();
    }
    if (this.$route.params.id) {
      await this.fetchResearch();
    }
  },
};
</script>
